import { useMemo } from 'react';
import { useProducts } from '@/data/Content/Products';
import { usePurchasedSku } from '@/data/Content/RepurchasePurchasedSku';
import {
	usePersonalizationDppSizes,
	usePersonalizationSeed,
} from '@/data/Content/RepurchaseCoupon';
import type { DppMemberPromotion } from '@/data/dpp/types';
import { useServerSideRuntime } from '@/data/context/origin';
import { ProductType } from '@/data/types/Product';
import { useUser } from '@/data/User';
import { getDppFirstName } from '@/utils/dppPromotion';
import { PersonalizationTokens } from '@/components/blocks/DpRepurchaseCoupon/fillPersonalization';

const SIZE_ATTRIBUTE = 'Talla';
const GENDER_ATTRIBUTE = 'Genero';
const PURCHASE_SIZE_SOURCE = 'PURCHASE';

const readSingleAttribute = (identifier: string, product?: ProductType) => {
	const values = product?.attributes?.find(
		(attribute) => attribute.identifier === identifier
	)?.values;
	if (values?.length !== 1) {
		return undefined;
	}
	const value = values[0]?.value;
	const single = Array.isArray(value) ? value[0] : value;
	return typeof single === 'string' ? single.trim() || undefined : undefined;
};

const toComparableSize = (value: string) => {
	const number = Number(value);
	return Number.isFinite(number) ? String(number) : value.trim().toUpperCase();
};

const readPurchasedSizeFromDpp = (product?: ProductType, sizes?: DppMemberPromotion['sizes']) => {
	const purchased = Object.values(sizes ?? {}).flatMap((size) =>
		size?.source === PURCHASE_SIZE_SOURCE && size.value?.trim()
			? [toComparableSize(size.value)]
			: []
	);
	if (!purchased.length) {
		return undefined;
	}
	return product?.items
		?.map((item) => readSingleAttribute(SIZE_ATTRIBUTE, item))
		.find((size) => !!size && purchased.includes(toComparableSize(size)));
};

export const usePersonalizationPurchase = () => {
	const { partNumber, orderId, placedAt, loading: loadingSeed } = usePersonalizationSeed();
	const { products, loading } = useProducts({ partNumber: partNumber ? [partNumber] : [] });
	const { sku } = usePurchasedSku({ orderId, partNumber });
	const { dppSizes } = usePersonalizationDppSizes();
	const product = products.at(0);
	const purchasedItem = product?.items?.find((item) => item.partNumber === sku);
	return {
		product,
		placedAt,
		loading: loadingSeed || loading,
		size:
			readPurchasedSizeFromDpp(product, dppSizes) ??
			readSingleAttribute(SIZE_ATTRIBUTE, purchasedItem) ??
			readSingleAttribute(SIZE_ATTRIBUTE, product),
		gender: readSingleAttribute(GENDER_ATTRIBUTE, product),
		brand: product?.manufacturer?.trim() || undefined,
	};
};

export const usePersonalizationFirstName = () => {
	const { personalization } = useServerSideRuntime();
	const { user } = useUser();
	return personalization?.firstName ?? getDppFirstName(user?.firstName);
};

export const usePersonalizationTokens = (): PersonalizationTokens => {
	const { personalization } = useServerSideRuntime();
	const firstName = usePersonalizationFirstName();
	const { product, size, brand } = usePersonalizationPurchase();
	const percentage = personalization?.percentage;

	return useMemo(
		() => ({
			nombre: firstName,
			porcentaje: percentage ? `${percentage}%` : undefined,
			marca: brand,
			talla: size,
			producto: product?.name,
		}),
		[firstName, percentage, brand, size, product?.name]
	);
};
